import AlternativeHeroSection from "@/components/Home/v2/AlternativeHeroSection";
import CtaBannerV2 from "@/components/Home/v2/CtaBannerV2";
import DarkPricingCompareSection from "@/components/Home/v2/DarkPricingCompareSection";
import DarkReasonsToSwitchSection from "@/components/Home/v2/DarkReasonsToSwitchSection";
import FaqV2 from "@/components/Home/v2/FaqV2";
import FeatureCompareAccordionsSection from "@/components/Home/v2/FeatureCompareAccordionsSection";
import FooterV2 from "@/components/Home/v2/FooterV2";
import NavbarV2 from "@/components/Home/v2/NavbarV2";
import ProblemVsPlatformSection from "@/components/Home/v2/ProblemVsPlatformSection";
import ReviewsMarqueeWithPainPointsSection from "@/components/Home/v2/ReviewsMarqueeWithPainPointsSection";
import StoriesMarqueeV2 from "@/components/Home/v2/StoriesMarqueeV2";
import GraphyHead from "@/components/organisms/GraphyHead";
import { getCalendlyLink } from "@/shared/utils/route.util";
import { getUTMFieldsFromLocalStorage, stringifyUTMParams } from "@/shared/utils/utm.util";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { parseCookies } from "nookies";
import { useSearchParams } from "next/navigation";
import {
  AlternativeV2DataType,
  AlternativeV2Section,
} from "@/shared/data/alternative.v2.data";

// Map the Next.js i18n URL locale to the country code used by
// `getCalendlyLink`. We prefer this over the `countryCode` prop (which
// comes from a cookie/IP lookup) because on first SSR the cookie is
// not yet set and the prop falls back to "IN" — which would route US
// visitors on `/us/...` to the India Calendly link.
const localeCountryCode: Record<string, string | undefined> = {
  us: "US",
  kr: "KR",
};

/**
 * AlternativeV2 — orchestrator for competitor-alternative marketing pages
 * (Kajabi Alternative, Teachable Alternative, ...).
 *
 * Layout: Navbar → hero → heterogeneous ordered section stack (reviews +
 * pain points, dark reasons, dark pricing compare, feature-compare
 * accordions, problem-vs-platform, stories, faq) → CTA banner → Footer.
 *
 * Section ordering is driven by the `sections` array on the data entry;
 * each item is a `{ kind, data }` discriminated union dispatched to the
 * matching HomeV2 component.
 */

export type AlternativeV2Props = {
  data: AlternativeV2DataType;
  countryCode: string;
  /** When true, render the shared HomeV2 navbar + footer instead of the
   *  minimal logo-and-demo header. The canonical `-alternative` (singular)
   *  routes wired from the navbar dropdown pass this; the `-alternatives`
   *  (plural) duplicates keep the stripped-chrome variant. */
  useHomeChrome?: boolean;
};

const renderSection = (section: AlternativeV2Section, idx: number) => {
  switch (section.kind) {
    case "reviews-marquee-pain-points":
      return (
        <ReviewsMarqueeWithPainPointsSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "dark-reasons":
      return (
        <DarkReasonsToSwitchSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "dark-pricing-compare":
      return (
        <DarkPricingCompareSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "feature-compare-accordions":
      return (
        <FeatureCompareAccordionsSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "problem-vs-platform":
      return (
        <ProblemVsPlatformSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "stories":
      return (
        <StoriesMarqueeV2
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "faq":
      return <FaqV2 key={`${section.kind}-${idx}`} {...section.data} />;
  }
};

const AlternativeV2 = ({
  data,
  countryCode,
  useHomeChrome = false,
}: AlternativeV2Props) => {
  const { locale } = useRouter();
  const params = useSearchParams();
  const gclid = params.get("gclid");
  // Locale wins over countryCode when it explicitly disagrees (i.e.
  // `/us/...` or `/kr/...`). For the default `en` locale we fall back
  // to the cookie-derived countryCode so UCE / IN / other-countries
  // routing still works for return visitors.
  const effectiveCountryCode =
    (locale && localeCountryCode[locale]) ?? countryCode;

  const handleNavBookDemo = () => {
    const url = data.bookDemoUrl ?? getCalendlyLink(effectiveCountryCode);
    const cookies = parseCookies();
    const utmFields = getUTMFieldsFromLocalStorage();
    utmFields["utm_content"] = gclid ?? "autobookdemos";
    const utmParams = stringifyUTMParams(utmFields);
    const separator = url.includes("?") ? "&" : "?";
    const finalUrl = cookies.countryCode !== "KR"
      ? `${url}${separator}${utmParams}`
      : url;
    window.open(finalUrl, "_blank")?.focus();
  };
  return (
    <>
      <GraphyHead {...data.seoData} countryCode={countryCode} />
      <div className="home-v2-scope relative">
        {useHomeChrome ? (
          <NavbarV2 />
        ) : (
          <header className="sticky top-0 z-bar w-full border-b border-transparent bg-white/60 backdrop-blur-md font-inter">
            <div className="container mx-auto px-4 py-4 sm:px-6 md:px-8 lg:px-12">
              <div className="mx-auto flex max-w-6xl items-center justify-between">
                <Link href="/" aria-label="Graphy home">
                  <Image
                    src="/images/graphy_logo_black.svg"
                    width={110}
                    height={32}
                    alt="Graphy"
                    priority
                    className="h-7 w-auto md:h-8"
                  />
                </Link>
                <button
                  onClick={handleNavBookDemo}
                  className="inline-flex items-center rounded-full bg-brandPrimary px-5 py-2.5 font-inter text-[14px] font-medium text-white transition-opacity hover:opacity-90 md:text-[15px]"
                >
                  Book a Demo
                </button>
              </div>
            </div>
          </header>
        )}
        <main className="relative z-10 flex flex-col">
          <AlternativeHeroSection {...data.hero} />
          {data.sections.map((section, idx) => renderSection(section, idx))}
          <CtaBannerV2 {...data.ctaBanner} />
        </main>
        {useHomeChrome && <FooterV2 {...data.footer} />}
      </div>
    </>
  );
};

export default AlternativeV2;
